import { useCallback, useMemo, type ReactNode } from 'react';
import Checkbox, { RangeCheckbox } from '@creditflowresearch/uilib/Checkbox';
import useCheckboxRangeSelection from '@creditflowresearch/uilib/hooks/useCheckboxRangeSelection';
import Tooltip from '@creditflowresearch/uilib/Tooltip';
import type { IColumn, ICellProps } from '@creditflowresearch/uilib/Table/DataTable';
import type { TSetDraft } from '@/creditflowresearch/application/hooks/useDraftState';
import type { IColumnCustomizationEntry } from '@/creditflowresearch/application/BondScreener/Sidebar/useColumnsCustomization';

import columnsStyles from '../columnsTabContent.module.scss';

interface IUseVisibleColumnProps {
  /** The entries as they are rendered — shift-click ranges are resolved against this order. */
  rows: IColumnCustomizationEntry[],
  getDraftColumns: () => IColumnCustomizationEntry[],
  setDraftColumns: TSetDraft<IColumnCustomizationEntry[]>,
}

const isEntryVisible = (entry: IColumnCustomizationEntry): boolean => entry.isVisible;
const isEntryLocked = (entry: IColumnCustomizationEntry): boolean => entry.isLocked;

const useVisibleColumn = ({ rows, getDraftColumns, setDraftColumns }: IUseVisibleColumnProps): IColumn => {
  const setEntriesVisible = useCallback((entries: IColumnCustomizationEntry[], isVisible: boolean): void => {
    const ids = new Set(entries.map((entry) => entry.id));

    setDraftColumns((prev) => prev.map((col) => (ids.has(col.id) ? { ...col, isVisible } : col)));
  }, [setDraftColumns]);

  const getCheckboxProps = useCheckboxRangeSelection<IColumnCustomizationEntry>({
    items: rows,
    isSelected: isEntryVisible,
    onChange: setEntriesVisible,
    isDisabled: isEntryLocked,
  });

  const toggleSelectAll = useCallback((): void => {
    const selectableColumns = getDraftColumns().filter((col) => !col.isLocked);
    const allSelected = selectableColumns.length > 0 && selectableColumns.every((col) => col.isVisible);

    setDraftColumns((prev) => prev.map((col) => (col.isLocked ? col : { ...col, isVisible: !allSelected })));
  }, [setDraftColumns, getDraftColumns]);

  const SelectAllHeadCell = useCallback((): ReactNode => {
    const selectableColumns = getDraftColumns().filter((col) => !col.isLocked);
    const allSelected = selectableColumns.length > 0 && selectableColumns.every((col) => col.isVisible);

    return (
      <th className={ columnsStyles.centeredCell }>
        <span className={ columnsStyles.selectAllHeaderInner }>
          <Tooltip content={ allSelected ? 'Deselect All' : 'Select All' }>
            <Checkbox
              dataTest="selectAllColumnsCheckbox"
              name="selectAllColumns"
              checked={ allSelected }
              onChange={ toggleSelectAll }
              className={ columnsStyles.checkbox }
            />
          </Tooltip>
        </span>
      </th>
    );
  }, [toggleSelectAll, getDraftColumns]);

  return useMemo((): IColumn => ({
    name: 'visible',
    title: '',
    width: 64,
    minWidth: 64,
    maxWidth: 64,
    isDraggable: false,
    className: columnsStyles.centeredCell,
    CustomHeadCell: SelectAllHeadCell,
    cellCallback: ({ row, rowIndex }: ICellProps<IColumnCustomizationEntry>): ReactNode => (
      <RangeCheckbox
        dataTest={ `columnCheckbox-${ row.id }` }
        name={ `column-${ row.id }` }
        checked={ row.isVisible }
        disabled={ row.isLocked }
        className={ columnsStyles.checkbox }
        { ...getCheckboxProps(rowIndex) }
      />
    ),
  }), [SelectAllHeadCell, getCheckboxProps]);
};

export default useVisibleColumn;
